import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Download, FileSpreadsheet, FileText, Link2, UploadCloud, X } from 'lucide-react';
import {
  buildUsecurePhishingReportFileName,
  calculatePhishingReportRates,
  downloadBlob,
  generateUsecurePhishingCampaignDocx,
  parseUsecureCampaignWorkbook,
  PHISHING_CAMPAIGN_REPORT_TEMPLATE_URL,
  type StoredUsecurePhishingCampaignReport,
  type UsecurePhishingCampaignStats,
} from '../services/usecurePhishingCampaignReportService';
import { getUsecureDashboard, type UsecureCampaign, type UsecureClientCampaigns } from '../services/usecureService';
import type { ProjectOption, TaskOption } from '../services/vulnerabilityScanDocxGenerator';

interface UsecurePhishingCampaignReportsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type ReportTab = 'reports' | 'prepare' | 'validate';
type UsecureCampaignOption = UsecureCampaign & { clientName: string; usecureCompanyId: string; learners?: UsecureClientCampaigns['learners'] };

const HISTORY_KEY = 'prisme_usecure_phishing_campaign_reports';
const AUTO_PHISH_LABEL = 'Auto Phish';

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function fmt(value?: number | null) {
  if (value === undefined || value === null) return 'Non disponible';
  return Number(value).toLocaleString('fr-CA');
}

function pct(value?: number | null) {
  if (value === undefined || value === null) return 'Non disponible';
  return `${value.toLocaleString('fr-CA', { maximumFractionDigits: 1 })} %`;
}

function toDateTimeLocal(value?: string | null) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 16);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

function isCampaignStartedWithinLastTwoMonths(campaign: UsecureCampaign) {
  if (!campaign.startDate) return false;
  const start = new Date(campaign.startDate);
  if (Number.isNaN(start.getTime())) return false;
  const now = new Date();
  const cutoff = new Date(now);
  cutoff.setMonth(cutoff.getMonth() - 2);
  return start >= cutoff && start <= now;
}

function isManualPhishingCampaignForReport(campaign: UsecureCampaign) {
  const searchable = [campaign.name, (campaign as any).type, campaign.deliveryMethod, (campaign as any).campaignType, (campaign as any).source]
    .filter(Boolean)
    .join(' ');
  return !new RegExp(AUTO_PHISH_LABEL.replace(/\s+/g, '\\s*'), 'i').test(searchable) && isCampaignStartedWithinLastTwoMonths(campaign);
}

function learnerDisplayName(learner: NonNullable<UsecureClientCampaigns['learners']>[number]) {
  return [learner.firstName, learner.lastName].filter(Boolean).join(' ').trim()
    || learner.name
    || learner.email
    || 'Utilisateur uSecure';
}

function actionFromSimulationResult(result: any) {
  const actions = [];
  if (result?.open) actions.push('Ouvert');
  if (result?.visit) actions.push('Clic');
  if (result?.compromise) actions.push('Soumission d’identifiants');
  if (result?.report) actions.push('Signalé');
  return actions.join(', ') || 'Résultat uSecure';
}

function normalizeIdentifier(value?: string | null) {
  return String(value || '').trim().toLowerCase();
}

function statsFromUsecureCampaign(campaign: UsecureCampaign, learners = [] as UsecureClientCampaigns['learners']): UsecurePhishingCampaignStats {
  const rawResults = Array.isArray((campaign as any).results) ? (campaign as any).results : [];
  const learnerResults = (learners || []).flatMap((learner) => learner.simulationResults?.filter((result) => String(result.simulationId) === String(campaign.id)).map((result) => ({
    ...result,
    name: learnerDisplayName(learner),
    email: learner.email || undefined,
    learnerId: learner.learnerId || learner.id || undefined,
  })) || []);
  const resultsByIdentity = new Map<string, any>();
  [...rawResults, ...learnerResults].forEach((result) => {
    const key = normalizeIdentifier(result?.learnerId || result?.email || result?.id || `${result?.name || result?.learnerName || ''}-${result?.open || ''}-${result?.visit || ''}-${result?.compromise || ''}`);
    if (!key) return;
    const previous = resultsByIdentity.get(key) || {};
    resultsByIdentity.set(key, { ...previous, ...result, open: previous.open || result?.open, visit: previous.visit || result?.visit, compromise: previous.compromise || result?.compromise, report: previous.report || result?.report });
  });
  const riskyUsers = [...resultsByIdentity.values()]
    .filter((result: any) => Boolean(result?.compromise || result?.visit))
    .slice(0, 100)
    .map((result: any) => ({
      name: result?.name || result?.learnerName || result?.email || 'Utilisateur uSecure',
      email: result?.email || undefined,
      action: actionFromSimulationResult(result),
    }));
  return {
    targetedUsers: campaign.recipientCount,
    emailsSent: campaign.sentCount,
    emailsOpened: campaign.openCount,
    clicks: campaign.visitCount,
    credentialSubmissions: campaign.compromiseCount,
    reports: campaign.reportCount,
    riskyUsers,
    detectedColumns: ['uSecure API', 'recipientCount', 'sentCount', 'openCount', 'visitCount', 'compromiseCount', 'reportCount'],
    sheetName: 'uSecure API',
    source: 'api',
  };
}

function loadHistory(): StoredUsecurePhishingCampaignReport[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveHistory(reports: StoredUsecurePhishingCampaignReport[]) {
  localStorage.setItem(HISTORY_KEY, JSON.stringify(reports.slice(0, 50)));
}

export default function UsecurePhishingCampaignReportsModal({ isOpen, onClose }: UsecurePhishingCampaignReportsModalProps) {
  const [activeTab, setActiveTab] = useState<ReportTab>('reports');
  const [reports, setReports] = useState<StoredUsecurePhishingCampaignReport[]>([]);
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [tasks, setTasks] = useState<TaskOption[]>([]);
  const [usecureCampaigns, setUsecureCampaigns] = useState<UsecureCampaignOption[]>([]);
  const [selectedUsecureCampaignId, setSelectedUsecureCampaignId] = useState('');
  const [loadingUsecureCampaigns, setLoadingUsecureCampaigns] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [selectedTaskId, setSelectedTaskId] = useState('');
  const [campaignName, setCampaignName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState(`${todayIso()}T17:00`);
  const [landingPageUrl, setLandingPageUrl] = useState('');
  const [emailScenario, setEmailScenario] = useState('Suspension temporaire de votre compte – Action requise');
  const [senderName, setSenderName] = useState('');
  const [senderEmail, setSenderEmail] = useState('');
  const [emailSubject, setEmailSubject] = useState('Suspension temporaire de votre compte – Action requise');
  const [templateFileName] = useState('Gabarit - Rapport de campagne d’hameçonnage 2025.docx');
  const [emailScreenshotName, setEmailScreenshotName] = useState('');
  const [emailScreenshotDataUrl, setEmailScreenshotDataUrl] = useState('');
  const [emailContainsQrCode, setEmailContainsQrCode] = useState(false);
  const [excelFileName, setExcelFileName] = useState('');
  const [emailScreenshotInputKey, setEmailScreenshotInputKey] = useState(0);
  const [excelInputKey, setExcelInputKey] = useState(0);
  const [advisorNotes, setAdvisorNotes] = useState('');
  const [recommendations, setRecommendations] = useState('');
  const [followupNote, setFollowupNote] = useState('');
  const [stats, setStats] = useState<UsecurePhishingCampaignStats>({ riskyUsers: [], detectedColumns: [] });
  const [statsValidated, setStatsValidated] = useState(false);
  const [message, setMessage] = useState('Préparez un rapport à partir d’une campagne uSecure complétée.');
  const [generating, setGenerating] = useState(false);

  const selectedProject = projects.find((project) => String(project.id) === selectedProjectId) || null;
  const selectedTask = tasks.find((task) => String(task.id) === selectedTaskId) || null;
  const selectedUsecureCampaign = usecureCampaigns.find((campaign) => campaign.id === selectedUsecureCampaignId) || null;
  const derivedClientName = selectedProject?.nom_client || selectedProject?.title || '';
  const clientNameForReport = selectedUsecureCampaign?.clientName || derivedClientName;
  const rates = useMemo(() => calculatePhishingReportRates(stats), [stats]);
  const hasCampaignStatsSource = Boolean(selectedUsecureCampaignId || excelFileName);
  const canGenerate = selectedProjectId && selectedTaskId && hasCampaignStatsSource && campaignName.trim() && startDate && endDate && landingPageUrl.trim() && senderName.trim() && senderEmail.trim() && emailSubject.trim() && templateFileName && emailScreenshotName && emailScreenshotDataUrl && statsValidated;

  useEffect(() => {
    if (!isOpen) return;
    setReports(loadHistory());
    setLoadingProjects(true);
    fetch('/api/projet-v2/projects', { credentials: 'include' })
      .then((response) => response.ok ? response.json() : Promise.reject(new Error('Impossible de charger les projets PRIsME.')))
      .then((data) => {
        const list = Array.isArray(data) ? data : data.projects || [];
        setProjects(list.map((project: any) => ({
          id: Number(project.id),
          title: project.title || project.nom || `Projet #${project.id}`,
          nom_client: project.nom_client || project.clientName || null,
          idclient: project.idclient || project.client_id || null,
          status: project.status || null,
        })).filter((project: ProjectOption) => Number.isFinite(project.id)));
      })
      .catch((error) => setMessage(error.message || 'Impossible de charger les projets.'))
      .finally(() => setLoadingProjects(false));

    setLoadingUsecureCampaigns(true);
    getUsecureDashboard('cache')
      .then((dashboard) => {
        const campaigns = (dashboard.clients || []).flatMap((client) => (client.activeCampaigns || []).map((campaign) => ({
          ...campaign,
          clientName: client.name,
          usecureCompanyId: client.usecureCompanyId,
          learners: client.learners || [],
        })));
        setUsecureCampaigns(campaigns.filter(isManualPhishingCampaignForReport).sort((a, b) => String(b.startDate || '').localeCompare(String(a.startDate || ''))));
      })
      .catch((error) => setMessage(error.message || 'Impossible de charger les campagnes uSecure.'))
      .finally(() => setLoadingUsecureCampaigns(false));
  }, [isOpen]);

  useEffect(() => {
    setTasks([]);
    setSelectedTaskId('');
    if (!selectedProjectId) return;
    setLoadingTasks(true);
    fetch(`/api/projet-v2/projects/${selectedProjectId}/tasks`, { credentials: 'include' })
      .then((response) => response.ok ? response.json() : Promise.reject(new Error('Impossible de charger les tâches du projet.')))
      .then((data) => {
        const list = Array.isArray(data) ? data : data.tasks || [];
        setTasks(list.map((task: any) => ({
          id: Number(task.id),
          titre: task.title || task.titre || task.nom || task.name || `Tâche #${task.id}`,
          status: task.status || task.statut || null,
        })).filter((task: TaskOption) => Number.isFinite(task.id)));
      })
      .catch((error) => setMessage(error.message || 'Impossible de charger les tâches.'))
      .finally(() => setLoadingTasks(false));
  }, [selectedProjectId]);

  function handleUsecureCampaignSelect(campaignId: string) {
    setSelectedUsecureCampaignId(campaignId);
    const campaign = usecureCampaigns.find((item) => item.id === campaignId);
    if (!campaign) return;
    setCampaignName(campaign.name || '');
    setStartDate(toDateTimeLocal(campaign.startDate));
    setEndDate(toDateTimeLocal(campaign.endDate));
    if (campaign.email?.fromName) setSenderName(campaign.email.fromName);
    if (campaign.email?.fromAddress) setSenderEmail(campaign.email.fromAddress);
    if (campaign.email?.subject) setEmailSubject(campaign.email.subject);
    if (campaign.email?.links?.[0]) setLandingPageUrl(campaign.email.links[0]);
    setStats(statsFromUsecureCampaign(campaign, campaign.learners));
    setStatsValidated(false);
    setExcelFileName('');
    setMessage('Résultats récupérés depuis uSecure. Complétez les détails du courriel qui ne sont pas exposés par l’API, puis confirmez les statistiques.');
  }

  async function handleExcelUpload(file?: File | null) {
    if (!file) return;
    setExcelFileName(file.name);
    setSelectedUsecureCampaignId('');
    try {
      const parsed = parseUsecureCampaignWorkbook(await file.arrayBuffer());
      setStats({ ...parsed, source: 'excel' });
      setStatsValidated(false);
      setMessage('Résultats Excel de secours importés. Confirmez les statistiques avant de générer le rapport.');
      setActiveTab('validate');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Impossible de lire le fichier Excel uSecure.');
    }
  }

  function clearExcelFallback() {
    setExcelFileName('');
    setExcelInputKey((key) => key + 1);
    setStats({ riskyUsers: [], detectedColumns: [] });
    setStatsValidated(false);
    setMessage('Excel de secours retiré. Sélectionnez une campagne uSecure ou importez le bon fichier Excel.');
  }

  async function handleEmailScreenshotUpload(file?: File | null) {
    if (!file) return;
    if (!['image/png', 'image/jpeg'].includes(file.type)) {
      setEmailScreenshotName('');
      setEmailScreenshotDataUrl('');
      setMessage('La figure 1 doit être une image réelle du courriel envoyé. Sélectionnez un fichier image PNG ou JPG compatible avec Word.');
      return;
    }
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(new Error('Impossible de lire la capture du courriel.'));
      reader.readAsDataURL(file);
    });
    setEmailScreenshotName(file.name);
    setEmailScreenshotDataUrl(dataUrl);
    setMessage('Capture du courriel chargée. Elle sera intégrée dans le document Word au-dessus de la figure 1.');
  }

  function clearEmailScreenshot() {
    setEmailScreenshotName('');
    setEmailScreenshotDataUrl('');
    setEmailScreenshotInputKey((key) => key + 1);
    setMessage('Capture d’écran retirée. Ajoutez la bonne capture de l’exemple du courriel avant de générer le rapport.');
  }

  async function buildReportBlob(report?: StoredUsecurePhishingCampaignReport) {
    return generateUsecurePhishingCampaignDocx(report || {
      clientName: clientNameForReport,
      campaignName,
      startDate,
      endDate,
      landingPageUrl,
      emailScenario,
      senderName,
      senderEmail,
      emailSubject,
      templateFileName,
      emailScreenshotName,
      emailScreenshotDataUrl,
      emailContainsQrCode,
      excelFileName,
      advisorNotes,
      recommendations,
      followupNote,
      projectTitle: selectedProject?.title,
      taskTitle: selectedTask?.titre,
      generatedBy: 'PRIsME',
      stats,
    });
  }

  async function createLinkedProjectDeliverable(docxFileName: string) {
    if (!selectedProjectId || !selectedTaskId) return null;
    const response = await fetch(`/api/projet-v2/projects/${selectedProjectId}/deliverables`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        title: `Rapport de campagne d’hameçonnage — ${campaignName}`,
        description: `Rapport Word généré depuis PRIsME pour la campagne uSecure « ${campaignName} ». Fichier DOCX: ${docxFileName}. Source statistiques: ${stats.source === 'api' ? 'uSecure API/cache' : `Excel de secours ${excelFileName || 'non fourni'}`}.`,
        status: 'delivered',
        visibility: 'internal',
        linked_task_id: Number(selectedTaskId),
        internal_notes: 'Source: rapport campagne hameçonnage uSecure. Aucun projet créé automatiquement; livrable ajouté à la tâche existante sélectionnée.',
      }),
    });
    const data = await response.json().catch(() => null);
    if (!response.ok) throw new Error(data?.error || 'Impossible d’ajouter le rapport comme livrable de tâche.');
    return data?.deliverable?.id ? Number(data.deliverable.id) : null;
  }

  function handleConfirmStats() {
    if (!selectedUsecureCampaignId && !excelFileName) {
      setMessage('Sélectionnez une campagne uSecure ou importez un Excel de secours avant de confirmer les statistiques.');
      return;
    }
    setStatsValidated(true);
    setMessage('Statistiques confirmées. Vous pouvez générer le rapport Word.');
  }

  async function handleGenerateReport() {
    if (!canGenerate) {
      setMessage(statsValidated ? 'Complétez les champs obligatoires avant de générer le rapport Word.' : 'Confirmez les statistiques récupérées depuis uSecure avant de générer le rapport Word.');
      return;
    }
    setGenerating(true);
    try {
      const docxFileName = buildUsecurePhishingReportFileName(clientNameForReport || 'client', campaignName);
      const blob = await buildReportBlob();
      downloadBlob(blob, docxFileName);
      const projectDeliverableId = await createLinkedProjectDeliverable(docxFileName);
      const stored: StoredUsecurePhishingCampaignReport = {
        id: `usecure-phishing-report-${Date.now()}`,
        status: 'generated',
        generatedAt: new Date().toISOString(),
        docxFileName,
        projectDeliverableId,
        excelSourceName: stats.source === 'api' ? 'uSecure API/cache' : excelFileName,
        clientName: clientNameForReport,
        campaignName,
        startDate,
        endDate,
        landingPageUrl,
        emailScenario,
        senderName,
        senderEmail,
        emailSubject,
        templateFileName,
        emailScreenshotName,
        emailScreenshotDataUrl,
        emailContainsQrCode,
        excelFileName,
        advisorNotes,
        recommendations,
        followupNote,
        projectTitle: selectedProject?.title || '',
        taskTitle: selectedTask?.titre || '',
        generatedBy: 'PRIsME',
        stats,
        clickRate: rates.clickRate,
        submissionRate: rates.submissionRate,
      };
      const nextReports = [stored, ...reports];
      setReports(nextReports);
      saveHistory(nextReports);
      setActiveTab('reports');
      setMessage(projectDeliverableId
        ? 'Rapport Word généré, ajouté à l’historique PRIsME et créé comme livrable de la tâche sélectionnée. Aucun projet n’a été créé.'
        : 'Rapport Word généré et ajouté à l’historique PRIsME. Ne pas créer de projet : le rapport est seulement rattaché à la tâche sélectionnée, si applicable.');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Impossible de générer le rapport Word.');
    } finally {
      setGenerating(false);
    }
  }

  async function handleDownloadReport(report: StoredUsecurePhishingCampaignReport) {
    const blob = await buildReportBlob(report);
    downloadBlob(blob, report.docxFileName);
  }

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 p-4 backdrop-blur-sm">
        <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.97 }} className="flex max-h-[94vh] w-full max-w-[min(1500px,96vw)] flex-col overflow-hidden rounded-3xl border border-slate-200 bg-slate-50 shadow-2xl">
          <header className="border-b border-slate-200 bg-gradient-to-r from-white via-blue-50 to-slate-50 p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-black uppercase tracking-wide text-blue-700">uSecure / uPhish · rapport seulement</p>
                <h2 className="mt-1 text-3xl font-black text-slate-900">Rapports de campagnes d’hameçonnage</h2>
                <p className="mt-2 max-w-4xl text-sm text-slate-600">Les campagnes restent exécutées dans uSecure. PRIsME sert à produire, gérer et conserver le rapport une fois la campagne terminée.</p>
              </div>
              <button onClick={onClose} className="rounded-xl p-2 text-slate-500 hover:bg-slate-100"><X className="h-6 w-6" /></button>
            </div>
            <div className="mt-5 grid gap-3 md:grid-cols-4">
              {[
                ['Rapports créés', reports.length.toLocaleString('fr-CA')],
                ['Aucun projet auto', '0'],
                ['Source', 'uSecure'],
                ['Rattachement', 'Projet / tâche'],
              ].map(([label, value]) => <div key={label} className="rounded-2xl border border-slate-200 bg-white p-4"><p className="text-xs font-bold uppercase text-slate-500">{label}</p><p className="mt-1 text-2xl font-black text-slate-900">{value}</p></div>)}
            </div>
          </header>

          <div className="border-b border-slate-200 bg-white px-6 py-3">
            <div className="flex flex-wrap gap-2">
              {[
                { key: 'reports', label: 'Rapports créés' },
                { key: 'prepare', label: 'Préparer le rapport' },
                { key: 'validate', label: 'Valider les résultats' },
              ].map((tab) => <button key={tab.key} type="button" onClick={() => setActiveTab(tab.key as ReportTab)} className={`rounded-xl px-4 py-2 text-sm font-black ${activeTab === tab.key ? 'bg-blue-600 text-white shadow' : 'border border-slate-200 bg-white text-slate-700 hover:bg-blue-50'}`}>{tab.label}</button>)}
            </div>
          </div>

          <main className="flex-1 overflow-y-auto p-6">
            {message && <div className="mb-5 rounded-2xl border border-blue-200 bg-blue-50 p-4 text-sm font-semibold text-blue-800">{message}</div>}

            {activeTab === 'reports' && (
              <section className="space-y-4">
                <div className="flex items-center justify-between gap-3">
                  <div><h3 className="text-xl font-black text-slate-900">Liste des rapports créés</h3><p className="text-sm text-slate-600">Historique local des rapports générés depuis PRIsME, sans exemples préchargés.</p></div>
                  <button type="button" onClick={() => setActiveTab('prepare')} className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-bold text-white hover:bg-blue-500">Préparer le rapport</button>
                </div>
                {reports.length ? <div className="grid gap-3">
                  {reports.map((report) => <article key={report.id} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                    <div className="grid gap-4 lg:grid-cols-[1fr_auto] lg:items-start">
                      <div>
                        <div className="flex flex-wrap items-center gap-2"><h4 className="text-lg font-black text-slate-900">{report.clientName || 'Client sans nom'}</h4><span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700">{report.status === 'generated' ? 'Généré' : 'Brouillon'}</span></div>
                        <p className="mt-1 text-sm text-slate-600">Campagne : {report.campaignName || 'Campagne sans nom'} · {report.startDate || '—'} au {report.endDate || '—'}</p>
                        <div className="mt-3 grid gap-2 text-sm text-slate-700 md:grid-cols-3">
                          <span>Utilisateurs ciblés : <strong>{fmt(report.stats.targetedUsers)}</strong></span>
                          <span>Taux de clic : <strong>{pct(report.clickRate)}</strong></span>
                          <span>Taux de soumission : <strong>{pct(report.submissionRate)}</strong></span>
                          <span>Rapport Word généré : <strong>{report.docxFileName}</strong></span>
                          <span>Fichier Excel source : <strong>{report.excelSourceName || 'Non disponible'}</strong></span>
                          <span>Tâche liée : <strong>{report.taskTitle || 'Aucune tâche sélectionnée'}</strong></span>
                          <span>Livrable projet : <strong>{report.projectDeliverableId ? `#${report.projectDeliverableId}` : 'Non créé'}</strong></span>
                        </div>
                      </div>
                      <button type="button" onClick={() => void handleDownloadReport(report)} className="inline-flex items-center justify-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-bold text-blue-800 hover:bg-blue-100"><Download className="h-4 w-4" />Télécharger le rapport</button>
                    </div>
                  </article>)}
                </div> : <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-slate-600">Aucun rapport créé. Sélectionnez un projet/tâche, une campagne uSecure et la capture du courriel pour générer le premier rapport.</div>}
              </section>
            )}

            {activeTab === 'prepare' && (
              <section className="grid gap-5 xl:grid-cols-[1.1fr_0.9fr]">
                <div className="space-y-5 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div><h3 className="text-xl font-black text-slate-900">Créer un rapport de campagne</h3><p className="text-sm text-slate-600">Méthode : rattacher le rapport au projet et à la tâche PRIsME, sélectionner la campagne uSecure, puis compléter seulement les détails du courriel non exposés par l’API.</p></div>
                  <div className="grid gap-4 md:grid-cols-2">
                    <label className="text-sm font-bold text-slate-700">Projet lié<select value={selectedProjectId} onChange={(event) => setSelectedProjectId(event.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"><option value="">Sélectionner le projet</option>{projects.map((project) => <option key={project.id} value={project.id}>{project.title}</option>)}</select>{loadingProjects && <span className="mt-1 block text-xs font-normal text-slate-500">Chargement des projets…</span>}</label>
                    <label className="text-sm font-bold text-slate-700">Tâche liée<select value={selectedTaskId} onChange={(event) => setSelectedTaskId(event.target.value)} disabled={!selectedProjectId || loadingTasks} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm disabled:bg-slate-100"><option value="">Sélectionner la tâche appropriée</option>{tasks.map((task) => <option key={task.id} value={task.id}>{task.titre}</option>)}</select></label>
                    <label className="text-sm font-bold text-slate-700 md:col-span-2">Campagne uSecure<select value={selectedUsecureCampaignId} onChange={(event) => handleUsecureCampaignSelect(event.target.value)} disabled={loadingUsecureCampaigns} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm disabled:bg-slate-100"><option value="">Sélectionner une campagne uSecure synchronisée</option>{usecureCampaigns.map((campaign) => <option key={campaign.id} value={campaign.id}>{campaign.clientName} — {campaign.name} — {campaign.status || 'statut inconnu'} — {campaign.startDate ? new Date(campaign.startDate).toLocaleDateString('fr-CA') : 'date inconnue'}</option>)}</select><span className="mt-1 block text-xs font-normal text-slate-500">Récupérer les résultats depuis uSecure remplit les dates et statistiques; l’Excel reste seulement un secours.</span></label>
                    <label className="text-sm font-bold text-slate-700">Nom de la campagne<input value={campaignName} onChange={(event) => setCampaignName(event.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" /></label>
                    <label className="text-sm font-bold text-slate-700">Date/Heure de début<input type="datetime-local" value={startDate} onChange={(event) => setStartDate(event.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" /></label>
                    <label className="text-sm font-bold text-slate-700">Date/Heure de fin<input type="datetime-local" value={endDate} onChange={(event) => setEndDate(event.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" /></label>
                    <label className="text-sm font-bold text-slate-700">URL de la page de destination<input type="url" value={landingPageUrl} onChange={(event) => setLandingPageUrl(event.target.value)} placeholder="https://..." className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" /></label>
                    <label className="text-sm font-bold text-slate-700">Prétexte / scénario<input value={emailScenario} onChange={(event) => setEmailScenario(event.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" /></label>
                    <label className="text-sm font-bold text-slate-700">Nom de l’expéditeur<input value={senderName} onChange={(event) => setSenderName(event.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" /></label>
                    <label className="text-sm font-bold text-slate-700">Adresse courriel de l’expéditeur<input type="email" value={senderEmail} onChange={(event) => setSenderEmail(event.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" /></label>
                    <label className="text-sm font-bold text-slate-700 md:col-span-2">Sujet<input value={emailSubject} onChange={(event) => setEmailSubject(event.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" /></label>
                  </div>
                  <div className="grid gap-3 md:grid-cols-2">
                    <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4 text-sm font-bold text-blue-800"><UploadCloud className="mb-2 h-5 w-5 text-blue-600" />Gabarit Word à utiliser<span className="mt-1 block text-xs font-normal text-blue-700">{templateFileName} — gabarit BZ versionné utilisé automatiquement depuis {PHISHING_CAMPAIGN_REPORT_TEMPLATE_URL}</span></div>
                    <label className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm font-bold text-slate-700"><FileSpreadsheet className="mb-2 h-5 w-5 text-emerald-600" />Excel de secours — Importer un Excel manuellement seulement si la campagne n’est pas disponible via uSecure<input key={excelInputKey} type="file" accept=".xlsx,.xls,.csv" className="mt-2 block w-full text-xs" onChange={(event) => void handleExcelUpload(event.target.files?.[0])} /><span className="mt-1 block text-xs font-normal text-slate-500">{excelFileName || 'Aucun Excel de secours importé'}</span>{excelFileName && <button type="button" onClick={clearExcelFallback} className="mt-3 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-black text-red-700 hover:bg-red-50">Retirer l’Excel</button>}</label>
                    <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm font-bold text-slate-700 md:col-span-2">Capture d’écran de l’exemple du courriel<input key={emailScreenshotInputKey} type="file" accept="image/png,image/jpeg" className="mt-2 block w-full text-xs" onChange={(event) => void handleEmailScreenshotUpload(event.target.files?.[0])} /><span className="mt-1 block text-xs font-normal text-slate-500">{emailScreenshotName || 'Aucune capture sélectionnée'} — l’image PNG/JPG sera intégrée au-dessus de la figure 1 dans le Word.</span><label className="mt-3 flex items-center gap-2 text-xs font-bold text-slate-700"><input type="checkbox" checked={emailContainsQrCode} onChange={(event) => setEmailContainsQrCode(event.target.checked)} className="rounded border-slate-300" />Le courriel envoyé contient un code QR visible</label>{emailScreenshotName && <button type="button" onClick={clearEmailScreenshot} className="mt-3 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-black text-red-700 hover:bg-red-50">Retirer la capture</button>}</div>
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    <label className="text-sm font-bold text-slate-700">Notes du conseiller<textarea value={advisorNotes} onChange={(event) => setAdvisorNotes(event.target.value)} rows={4} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" /></label>
                    <label className="text-sm font-bold text-slate-700">Recommandations / prochaines actions facultatives<textarea value={recommendations} onChange={(event) => setRecommendations(event.target.value)} rows={4} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" /></label>
                    <label className="text-sm font-bold text-slate-700 md:col-span-2">Ajouter une note de suivi<textarea value={followupNote} onChange={(event) => setFollowupNote(event.target.value)} rows={3} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" /></label>
                  </div>
                </div>

                <aside className="space-y-5">
                  <button type="button" onClick={() => setActiveTab('validate')} className="w-full rounded-xl bg-blue-600 px-4 py-3 text-sm font-black text-white hover:bg-blue-500">Valider les résultats</button>
                </aside>
              </section>
            )}

            {activeTab === 'validate' && (
              <section className="grid gap-5 xl:grid-cols-[1fr_360px]">
                <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <h3 className="text-xl font-black text-slate-900">Valider les résultats</h3>
                  <p className="mt-1 text-sm text-slate-600">PRIsME récupère les statistiques depuis la campagne uSecure sélectionnée. L’Excel de secours sert seulement si la campagne n’est pas disponible via uSecure.</p>
                  <div className="mt-5 grid gap-3 md:grid-cols-3">
                    {[
                      ['nombre d’utilisateurs ciblés', fmt(stats.targetedUsers)],
                      ['courriels envoyés', fmt(stats.emailsSent)],
                      ['courriels ouverts', fmt(stats.emailsOpened)],
                      ['clics', fmt(stats.clicks)],
                      ['soumissions d’identifiants', fmt(stats.credentialSubmissions)],
                      ['signalements', fmt(stats.reports)],
                      ['Taux de clic', pct(rates.clickRate)],
                      ['Taux de soumission', pct(rates.submissionRate)],
                      ['utilisateurs à risque', fmt(stats.riskyUsers.length)],
                    ].map(([label, value]) => <div key={label} className="rounded-2xl border border-slate-200 bg-slate-50 p-4"><p className="text-xs font-bold uppercase text-slate-500">{label}</p><p className="mt-1 text-2xl font-black text-slate-900">{value}</p></div>)}
                  </div>
                  <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <h4 className="font-black text-slate-900">Utilisateurs à risque ou ayant cliqué</h4>
                    <div className="mt-3 max-h-60 overflow-y-auto rounded-xl border border-slate-200 bg-white">
                      {stats.riskyUsers.length ? stats.riskyUsers.slice(0, 40).map((user, index) => <div key={`${user.email || user.name}-${index}`} className="grid gap-2 border-b border-slate-100 px-3 py-2 text-sm text-slate-700 md:grid-cols-[1fr_1fr_auto]"><span>{user.name}</span><span>{user.email || '—'}</span><strong>{user.action}</strong></div>) : <p className="p-4 text-sm text-slate-500">Aucun utilisateur à risque détecté dans les colonnes importées.</p>}
                    </div>
                  </div>
                </div>
                <aside className="space-y-4">
                  <div className={`rounded-2xl border p-5 text-sm ${statsValidated ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-blue-200 bg-blue-50 text-blue-800'}`}>
                    <div className="flex items-center gap-2 font-black"><CheckCircle2 className="h-4 w-4" />{statsValidated ? 'Statistiques confirmées' : 'Validation requise'}</div>
                    <p className="mt-2">Vérifiez les valeurs récupérées depuis uSecure ou l’Excel de secours, puis confirmez-les pour débloquer la génération du rapport.</p>
                  </div>
                  <button type="button" onClick={handleConfirmStats} disabled={(!selectedUsecureCampaignId && !excelFileName) || statsValidated} className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-3 text-sm font-black text-white hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-50"><CheckCircle2 className="h-4 w-4" />{statsValidated ? 'Statistiques confirmées' : 'Confirmer les statistiques'}</button>
                  <button type="button" onClick={() => void handleGenerateReport()} disabled={generating || !canGenerate} className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-3 text-sm font-black text-white hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-50"><FileText className="h-4 w-4" />{generating ? 'Génération…' : 'Générer le rapport Word'}</button>
                  <button type="button" onClick={() => setActiveTab('prepare')} className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 hover:bg-slate-50"><Link2 className="h-4 w-4" />Corriger les sources</button>
                </aside>
              </section>
            )}
          </main>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
